/**
 * Карточка решения (A4, A9): рамка чернилами, вопрос Piazzolla 22px,
 * варианты — полноширинные кнопки с текстом слева. Недоступные —
 * `disabled`, зачёркнуты, причина в самом тексте («Учиться — рядом нет
 * школы»). «Что это значит?» раскрывает под каждым вариантом вероятные
 * последствия словами, без чисел (`hint.<kind>.<optionId>`).
 *
 * В одиночной игре таймера нет. На автопилоте — предпросмотр: вариант
 * автовыбора (`timeoutOption`) подсвечен, строка в моно охрой считает
 * секунды раз в секунду, кнопка «Перехватить» отменяет отсчёт; по
 * истечении — `onAnswer(timeoutOption)`. Тап по варианту во время
 * предпросмотра — тоже перехват: отвечает игрок.
 */
import type { Decision } from '@life/engine';
import { t } from '@life/i18n';
import { useEffect, useState } from 'preact/hooks';

/** Сколько секунд показывается готовый выбор автопилота. */
export const PREVIEW_SECONDS = 3;

export interface DecisionCardProps {
  decision: Decision;
  autopilot: boolean;
  onAnswer: (optionId: string) => void;
}

/** Ключ пояснения варианта: `hint.<kind>.<optionId>`. */
export function hintKey(kind: Decision['kind'], optionId: string): string {
  return `hint.${kind}.${optionId}`;
}

export function DecisionCard({ decision, autopilot, onAnswer }: DecisionCardProps) {
  const [hints, setHints] = useState(false);
  /** Перехвачен ли предпросмотр автопилота (для этого решения). */
  const [intercepted, setIntercepted] = useState(false);
  const [left, setLeft] = useState(PREVIEW_SECONDS);
  const preview = autopilot && !intercepted;

  // Новое решение — новый предпросмотр.
  useEffect(() => {
    setIntercepted(false);
    setLeft(PREVIEW_SECONDS);
  }, [decision.id]);

  useEffect(() => {
    if (!preview) return;
    let remaining = PREVIEW_SECONDS;
    setLeft(remaining);
    const id = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setLeft(remaining);
        return;
      }
      clearInterval(id);
      onAnswer(decision.timeoutOption);
    }, 1000);
    return () => clearInterval(id);
    // Отсчёт привязан к решению и режиму, не к ссылке на onAnswer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, decision.id]);

  const timeoutLabel = decision.options.find((o) => o.id === decision.timeoutOption)?.labelKey;

  return (
    <section class="decision" aria-live="polite">
      <h2 class="t">{t(decision.promptKey)}</h2>
      <div class="opts">
        {decision.options.map((o) => {
          const name = t(o.labelKey);
          const previewed = preview && o.id === decision.timeoutOption;
          return (
            <div key={o.id} class="opt">
              <button
                type="button"
                class={['btn', 'block', o.available ? '' : 'na', previewed ? 'preview' : ''].filter(Boolean).join(' ')}
                disabled={!o.available}
                aria-current={previewed ? 'true' : undefined}
                onClick={o.available ? () => onAnswer(o.id) : undefined}
              >
                {o.available
                  ? name
                  : t('ui.decision.unavailable', { name, reason: t(o.reasonKey ?? 'reason.unavailable') })}
              </button>
              {hints && <p class="hint">{t(hintKey(decision.kind, o.id))}</p>}
            </div>
          );
        })}
      </div>
      <div class="decision-foot">
        <button type="button" class="btn ghost" aria-expanded={hints} onClick={() => setHints((v) => !v)}>
          {t('ui.decision.hint')}
        </button>
        {preview && (
          <>
            <span class="timer">
              {t('ui.decision.auto', { option: timeoutLabel ? t(timeoutLabel) : decision.timeoutOption, s: left })}
            </span>
            <button type="button" class="btn" onClick={() => setIntercepted(true)}>
              {t('ui.decision.intercept')}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
